import React from 'react';

import { ReorderEntryMap } from 'features/menu/hooks/types';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import { ItemAvailabilityStatus } from 'enums/menu';
import { useCart } from 'hooks/use-cart';
import useIsComponentMounted from 'hooks/use-is-component-mounted';
import { useMapToFrontendCartEntry } from 'state/order/hooks/use-map-to-frontend-cart-entry';

interface IUseReorderEntriesReturnType {
  reorderEntriesMap: ReorderEntryMap;
  checkingAvailability: boolean;
  hasSomeAvailableEntry: boolean;
}

export const useReorderEntries = (
  reorderCartEntries: IBackendCartEntries[] | undefined
): IUseReorderEntriesReturnType => {
  const [checkingAvailability, setCheckingAvailability] = React.useState<boolean>(false);
  const [reorderEntriesMap, setReorderEntriesMap] = React.useState<ReorderEntryMap>({});
  const { cartEntries } = useCart();
  const mapToFrontendCartEntry = useMapToFrontendCartEntry();
  const isMounted = useIsComponentMounted();

  const hasSomeAvailableEntry = React.useMemo(() => {
    // Checks if there is at least one available entry in the order,
    // if not, disable reordering.
    return Object.values(reorderEntriesMap)
      .map(entry => entry.itemAvailabilityStatus)
      .some(status => status === ItemAvailabilityStatus.AVAILABLE);
  }, [reorderEntriesMap]);

  React.useEffect(() => {
    const getReorderEntries = async () => {
      const reorderEntries: ReorderEntryMap = {};

      if (!reorderCartEntries) {
        return;
      }

      setCheckingAvailability(true);

      for (const reorderCartEntry of reorderCartEntries) {
        // Check if component is still mounted before checking availability.
        if (!isMounted) {
          return;
        }

        const {
          cartEntry: frontendEntry,
          status: itemAvailabilityStatus,
          data: menuObject,
        } = await mapToFrontendCartEntry(reorderCartEntry, cartEntries);

        reorderEntries[reorderCartEntry.lineId] = {
          frontendEntry,
          itemAvailabilityStatus,
          menuObject,
        };
      }

      // Check if component is still mounted before updating the state.
      if (!isMounted) {
        return;
      }

      setReorderEntriesMap(reorderEntries);
      setCheckingAvailability(false);
    };

    getReorderEntries();
  }, [cartEntries, isMounted, mapToFrontendCartEntry, reorderCartEntries]);

  return { reorderEntriesMap, checkingAvailability, hasSomeAvailableEntry };
};
